import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { FormControl } from 'react-bootstrap';

// Converts a value that may arrive as a number, string, '' or null/undefined
// into the string form we hold in the input's local draft. Anything that
// doesn't represent a finite number collapses to '' so the input renders
// empty rather than literally "NaN" / "null".
const toDraftString = (v) => {
  if (v === '' || v === null || v === undefined) return '';
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? String(n) : '';
};

/**
 * Controlled numeric input that keeps a local string draft while focused.
 *
 * The naive `value={number}` + `onChange={+e.target.value}` pattern used
 * throughout the DesignTool has a subtle UX bug: deleting the last digit
 * yields '', which `+` coerces to 0, which the parent stores and React
 * re-renders as "0" — making the field appear to reject backspace. Other
 * variants of the bug:
 *   - Handlers that early-return on '' simply leave the parent value as
 *     it was, and React re-renders the field with the stale value.
 *   - Display helpers that snap a missing value back to a default (e.g.
 *     trunkHeight -> 10) cause the field to bounce to that default on
 *     clear.
 *
 * This component sidesteps all of the above by holding the live string
 * the user is typing in local state while focused. The parent is still
 * notified on every keystroke that produces a finite number, so anything
 * derived from the value (canvas, other inputs, derived calculations)
 * keeps reacting in real time. Empty commits propagate as `undefined`
 * unless `skipEmptyCommit` is true, in which case the parent simply
 * isn't called and the field's blur behaviour determines what the user
 * ends up with.
 *
 * On blur the draft re-syncs from `value` so any parent normalization
 * (defaults, clamping) becomes visible.
 */
function NumberCellInput({
  id,
  value,
  disabled,
  size,
  min,
  className,
  skipEmptyCommit,
  onCommit,
}) {
  const [draft, setDraft] = useState(() => toDraftString(value));
  const focusedRef = useRef(false);

  useEffect(() => {
    if (!focusedRef.current) setDraft(toDraftString(value));
  }, [value]);

  const handleChange = (e) => {
    const raw = e.target.value;
    setDraft(raw);
    if (raw === '') {
      if (!skipEmptyCommit) onCommit(undefined);
      return;
    }
    const parsed = Number(raw);
    if (Number.isFinite(parsed)) onCommit(parsed);
  };

  return (
    <FormControl
      id={id}
      type="number"
      size={size}
      min={min}
      className={className}
      value={draft}
      disabled={disabled}
      onFocus={() => {
        focusedRef.current = true;
      }}
      onBlur={() => {
        focusedRef.current = false;
        setDraft(toDraftString(value));
      }}
      onChange={handleChange}
    />
  );
}

NumberCellInput.propTypes = {
  id: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  disabled: PropTypes.bool,
  size: PropTypes.string,
  min: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  className: PropTypes.string,
  // When true, an empty input is treated as "no commit" — the parent's
  // value isn't touched. Useful for required fields (e.g. setbacks) where
  // the parent should never receive `undefined`; combined with the
  // draft-on-focus behaviour, the user still gets a free editing
  // experience and the field snaps back to the parent value on blur.
  skipEmptyCommit: PropTypes.bool,
  onCommit: PropTypes.func.isRequired,
};

NumberCellInput.defaultProps = {
  value: '',
  disabled: false,
  size: 'sm',
  min: undefined,
  className: undefined,
  skipEmptyCommit: false,
};

export default NumberCellInput;
